export const LANGUAGE_STORAGE_KEY = 'language';
export const DEFAULT_LANGUAGE = 'en';
export const PAGE_LANGUAGE = 'en';

export const TRANSLATE_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', rtl: false },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', rtl: true },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', rtl: true },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', rtl: false },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', rtl: false },
  { code: 'ps', name: 'Pashto', nativeName: 'پښتو', rtl: true },
  { code: 'sd', name: 'Sindhi', nativeName: 'سنڌي', rtl: true },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', rtl: true },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', rtl: false },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', rtl: false },
  { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '简体中文', rtl: false },
  { code: 'es', name: 'Spanish', nativeName: 'Español', rtl: false },
  { code: 'fr', name: 'French', nativeName: 'Français', rtl: false },
  { code: 'de', name: 'German', nativeName: 'Deutsch', rtl: false },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', rtl: false },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', rtl: false },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', rtl: false },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', rtl: false },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', rtl: false },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', rtl: false },
  { code: 'ko', name: 'Korean', nativeName: '한국어', rtl: false },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', rtl: false },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', rtl: false },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', rtl: false },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', rtl: false },
];

const RTL_CODES = new Set(TRANSLATE_LANGUAGES.filter((lang) => lang.rtl).map((lang) => lang.code));

export function isRtlLanguage(code) {
  return RTL_CODES.has(code);
}

export function getSavedLanguage() {
  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved && TRANSLATE_LANGUAGES.some((lang) => lang.code === saved)) {
      return saved;
    }
  } catch {
    // ignore
  }
  return DEFAULT_LANGUAGE;
}

export function applyDocumentDirection(lang) {
  const rtl = isRtlLanguage(lang);
  document.documentElement.dir = rtl ? 'rtl' : 'ltr';
  document.documentElement.lang = lang === 'zh-CN' ? 'zh' : lang;
  document.body?.classList.toggle('lang-rtl', rtl);
}

function cookieDomains() {
  const hostname = window.location.hostname;
  const domains = [''];
  if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
    domains.push(hostname);
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      domains.push(`.${parts.slice(-2).join('.')}`);
    }
  }
  return domains;
}

function writeGoogTransCookie(value) {
  cookieDomains().forEach((domain) => {
    const domainPart = domain ? `; domain=${domain}` : '';
    document.cookie = `googtrans=${value}; path=/${domainPart}`;
  });
}

function clearGoogTransCookie() {
  cookieDomains().forEach((domain) => {
    const domainPart = domain ? `; domain=${domain}` : '';
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
  });
}

function persistLanguage(lang) {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // ignore
  }
}

export function resetDisplayLanguage() {
  persistLanguage(DEFAULT_LANGUAGE);
  clearGoogTransCookie();
  applyDocumentDirection(DEFAULT_LANGUAGE);
}

export function changeDisplayLanguage(lang, { reload = true } = {}) {
  const next = TRANSLATE_LANGUAGES.some((item) => item.code === lang) ? lang : DEFAULT_LANGUAGE;
  persistLanguage(next);
  applyDocumentDirection(next);

  if (next === DEFAULT_LANGUAGE) {
    clearGoogTransCookie();
  } else {
    writeGoogTransCookie(`/${PAGE_LANGUAGE}/${next}`);
  }

  if (reload) {
    window.location.reload();
  }
}

function ensureTranslateHost() {
  let host = document.getElementById('google_translate_element');
  if (!host) {
    host = document.createElement('div');
    host.id = 'google_translate_element';
    host.className = 'google-translate-host notranslate';
    host.setAttribute('aria-hidden', 'true');
    document.body.appendChild(host);
  }
  return host;
}

export function initGoogleTranslate() {
  if (typeof window === 'undefined') return;

  const lang = getSavedLanguage();
  applyDocumentDirection(lang);

  if (lang === DEFAULT_LANGUAGE) {
    clearGoogTransCookie();
  } else {
    writeGoogTransCookie(`/${PAGE_LANGUAGE}/${lang}`);
  }

  ensureTranslateHost();

  window.googleTranslateElementInit = function googleTranslateElementInit() {
    if (!window.google?.translate?.TranslateElement) return;
    new window.google.translate.TranslateElement(
      {
        pageLanguage: PAGE_LANGUAGE,
        includedLanguages: TRANSLATE_LANGUAGES.map((item) => item.code).join(','),
        autoDisplay: false,
      },
      'google_translate_element'
    );
  };

  if (!document.getElementById('google-translate-script')) {
    const script = document.createElement('script');
    script.id = 'google-translate-script';
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
  }
}
